import { CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Spinner } from '@/Domain/components/Shared/Spinner';

type Props = {
  isOpen: boolean;
  onOpenChange: () => void;
  onCallback: (cancellationReason: string) => void;
};

export const CancelSubscriptionDialog = ({ isOpen, onOpenChange, onCallback }: Props) => {
  const { t } = useTranslation('subscription_page');

  const [isActionPending, setIsActionPending] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  const handleCallback = () => {
    setIsActionPending(true);

    onCallback(cancellationReason);
  };

  useEffect(() => {
    if (!isOpen) {
      setIsActionPending(false);
      setCancellationReason('');
    }
  }, [isOpen]);

  const isCancelDisabled = isActionPending || !cancellationReason.trim();

  return (
    <AlertDialog
      open={isActionPending ? true : isOpen}
      onOpenChange={() => !isActionPending && onOpenChange()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <CircleAlert className="bg-red-100 rounded-full text-red-500 p-1.5 box-content size-4" />
            {t('cancel_subscription_dialog.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('cancel_subscription_dialog.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <textarea
          value={cancellationReason}
          onChange={(e) => setCancellationReason(e.target.value)}
          placeholder={t('cancel_subscription_dialog.reason_placeholder')}
          disabled={isActionPending}
          className="py-3 px-4 flex w-full rounded-xl border border-border focus-within:!ring-[3px] text-sm placeholder-foreground-subtle focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 custom-scroll overflow-auto resize-none focus-within:ring-ring/25 focus-within:!border-foreground"
          rows={3}
        />
        <Separator />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isActionPending}>
            <Button size="sm" variant="secondary" disabled={isActionPending}>
              {t('cancel_subscription_dialog.button.do_not_cancel')}
            </Button>
          </AlertDialogCancel>
          <Button size="sm" variant="danger" onClick={handleCallback} disabled={isCancelDisabled}>
            {isActionPending && <Spinner className="text-white h-4" />}
            {isActionPending
              ? t('cancel_subscription_dialog.button.processing')
              : t('cancel_subscription_dialog.button.cancel')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
